import { useMemo } from 'react'
import { Modal, Stack, Table } from 'react-bootstrap'

import styled from 'styled-components'

import { useTitle } from '@lib/web'
import { DataRecords } from '@piv/lib/rest-queries'
import { DataFromDate } from '@piv/portal/primitives/DataFromDate'
import { DetailTable } from '@piv/portal/primitives/DetailTable'
import { useFullscreenSpinner } from '@piv/portal/primitives/FullscreenSpinner'
import { InlineError } from '@piv/portal/primitives/InlineError'
import { PackageLink } from '@piv/portal/primitives/PackageLink'

interface Props {
  dataPackageId: string
  recordId: string | false
  onClosed: () => void
}

export function RecordModal({ dataPackageId, recordId, onClosed }: Props) {
  useTitle(recordId ? `${recordId} Preview` : '')

  const record = DataRecords.useRecord(dataPackageId, recordId)

  useFullscreenSpinner(record.isPending)

  const samples = record.data?.samples
  const samplesHeader = useMemo(() => {
    if (!samples) {
      return []
    }

    const headers: string[] = []
    const headersWithData: string[] = []
    for (const sample of samples) {
      for (const key in sample) {
        if (!headers.includes(key)) {
          headers.push(key)
        }
        if (!headersWithData.includes(key) && !!sample[key]) {
          headersWithData.push(key)
        }
      }
    }

    headersWithData.sort()
    headers.sort()

    // We want keys which actually have SOME data to be displayed first for ergonic reasons. Other fields are still listed at the end
    const prioritisedHeaders: string[] = [...headersWithData]
    for (const header of headers) {
      if (prioritisedHeaders.includes(header)) {
        continue
      }

      prioritisedHeaders.push(header)
    }

    return prioritisedHeaders
  }, [samples])

  return (
    <Modal
      show={!!recordId && !!dataPackageId && record.isFetched}
      onHide={onClosed}
      size="xl"
      enforceFocus
      animation
      aria-labelledby="preview-modal-title"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title id="preview-modal-title">
            {record.data?.displayName}
          </Modal.Title>

          <SubHeading>{record.data?.id}</SubHeading>
        </div>
      </Modal.Header>

      <Modal.Body>
        <InlineError isError={record.isError} />

        <DetailTable title="Summary" style={{ marginBottom: '1rem' }}>
          <DetailTable.Item
            label="Data back to"
            value={<DataFromDate date={record.data?.record.dateFrom ?? null} />}
          />
          <DetailTable.Item
            label="Brands"
            value={record.data?.record.brandDisplayName}
          />
          <DetailTable.Item
            label="Regions"
            value={record.data?.record.regionDisplayName}
          />
          <DetailTable.Item
            label="Currencies"
            value={[
              record.data?.record.currency1DisplayName,
              record.data?.record.currency2DisplayName,
            ]}
            whenEmpty="display"
          />

          <DetailTable.Item
            label="Clearing Type"
            value={record.data?.record.additionalData.clearingType?.displayName}
          />
          <DetailTable.Item
            label="Price Type"
            value={record.data?.record.additionalData.priceType?.displayName}
          />
          <DetailTable.Item
            label="Pricing Frequencies"
            value={[
              record.data?.record.additionalData.pricing_frequency1
                ?.displayName,
              record.data?.record.additionalData.pricing_frequency2
                ?.displayName,
            ]}
          />
          <DetailTable.Item
            label="Reset Frequencies"
            value={[
              record.data?.record.additionalData.reset_frequency1?.displayName,
              record.data?.record.additionalData.reset_frequency2?.displayName,
            ]}
          />
          <DetailTable.Item
            label="Payment Frequency"
            value={
              record.data?.record.additionalData.payment_frequency?.displayName
            }
          />
          <DetailTable.Item
            label="Day Count"
            value={[
              record.data?.record.additionalData.dayCount1?.displayName,
              record.data?.record.additionalData.dayCount2?.displayName,
            ]}
          />
          <DetailTable.Item
            label="Period"
            value={[
              record.data?.record.additionalData.period1?.displayName,
              record.data?.record.additionalData.period2?.displayName,
            ]}
          />

          <DetailTable.Item
            label="Strike"
            value={formatStrike(
              record.data?.record.additionalData.strikeUnit,
              record.data?.record.additionalData.strikeValue
            )}
          />
          <DetailTable.Item
            label="Settlement Method"
            value={
              record.data?.record.additionalData.settlementMethod?.displayName
            }
          />
          <DetailTable.Item
            label="Settlement Index"
            value={[
              record.data?.record.additionalData.settlementIndex1?.displayName,
              record.data?.record.additionalData.settlementIndex2?.displayName,
            ]}
          />
          <DetailTable.Item
            label="In Data Packages"
            value={
              <Stack gap={1}>
                {record.data?.dataPackages.map(({ id, displayName }) => (
                  <PackageLink key={id} id={id} name={displayName} />
                ))}
              </Stack>
            }
          />
        </DetailTable>

        <DetailTable.Title>Tick Data Preview</DetailTable.Title>
        <SampleTableContainer>
          <Table size="sm" hover id={'tick-data'}>
            <thead>
              <tr>
                {samplesHeader.map((key) => (
                  <th style={{ padding: '0.5rem 0.75rem' }} key={key}>
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <SampleTableTBody>
              {samples?.map((sample, i) => (
                <tr key={i}>
                  {samplesHeader.map((key) => (
                    <td key={key}>{sample[key]}</td>
                  ))}
                </tr>
              ))}
            </SampleTableTBody>
          </Table>
        </SampleTableContainer>
      </Modal.Body>
    </Modal>
  )
}

const SubHeading = styled.h6`
  font-size: 0.7rem;
  font-weight: 400;
  opacity: 0.8;
`

const SampleTableContainer = styled.div`
  overflow-x: auto;
`

const SampleTableTBody = styled.tbody`
  td {
    white-space: nowrap;
    overflow: hidden;
    padding: 0.5rem 0.75rem;
  }
`

type StrikeUnit = DataRecords.Types.DataRecord['additionalData']['strikeUnit']
export function formatStrike(
  unit: StrikeUnit | undefined | null,
  value: number | undefined | null
) {
  if (!unit || !value) {
    return ''
  }

  switch (unit) {
    case 'PERCENT':
      return `${value}%`
    case 'BASIS_POINTS':
      return `${value} bps`
    case 'ATM':
      return 'ATM'
  }

  console.error('Strike with unexpected unit', unit, 'could not be formatted')

  return ''
}
